import { CdkPortalOutlet } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  HostBinding,
  HostListener,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { dynamicAnimations } from 'app/shared/animations/animations';
import { Subject } from 'rxjs';

import { ModalConfiguration, newModalConfiguration } from '../../models/modal-configuration.model';
import { DynamicLoaderService } from '../../services/dynamic-loader.service';
import { HttpLoaderIndicatorService } from 'app/shared/layout/services/http-loader-indicator.service';

@Component({
  selector: 'dynamic-dynamic-container',
  templateUrl: './dynamic-container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [dynamicAnimations],
  host: {
    '[class]': 'modalConfiguration.panelClass',
  },
  standalone: false,
})
export class DynamicContainerComponent<C extends object> implements OnDestroy {
  @ViewChild('container', { read: CdkPortalOutlet, static: false })
  container?: CdkPortalOutlet;
  isLoading = this.loadingService.loading$;

  dynamicLoaderService?: DynamicLoaderService;
  containerId?: number;
  componentRef?: ComponentRef<C>;
  animationEnd = new Subject<boolean>();

  activeClass = false;

  modalConfiguration: ModalConfiguration = newModalConfiguration();

  constructor(
    private elementRef: ElementRef,
    private ref: ChangeDetectorRef,
    private loadingService: HttpLoaderIndicatorService
  ) {}

  @HostBinding('class.active') get a_class(): boolean {
    return this.activeClass;
  }

  @HostListener('document:click', ['$event'])
  clickEvent(event: any): void {
    if (this.modalConfiguration.outsideClick) {
      const clickedOutside = event.target === this.elementRef.nativeElement;
      if (clickedOutside) {
        this.close();
      }
    }
  }

  ngOnDestroy(): void {
    if (this.componentRef) {
      this.componentRef.destroy();
    }
  }

  onCloseClick(): void {
    this.close();
  }

  attachChildComponent(childComponent: any, data: any): void {
    this.ref.detectChanges();
    if (!this.container) {
      return;
    }
    if (this.container.hasAttached()) {
      return;
    }
    this.componentRef = this.container.attachComponentPortal(childComponent);
    Object.assign(this.componentRef?.instance, data);
  }

  close(): void {
    this.startExitAnimation();
  }

  removeContainer(): void {
    if (this.containerId) {
      this.dynamicLoaderService?.removeComponentFromBody(this.containerId);
    }
  }

  onAnimationDone(event: any): void {
    if (event.toState === 'exit') {
      this.removeContainer();
    }
    this.animationEnd.next(true);
  }

  onAnimationStart(event: any): void {
    this.activeClass = event.toState !== 'exit';
  }

  startExitAnimation(): void {
    this.modalConfiguration.animation = 'exit';
    this.activeClass = false;
    this.ref.markForCheck();
  }
}
